(function(global) {
  'use strict';

  var MIN_MODEL_YEAR = 1990;
  var STORAGE_KEY = 'briVehicleFinderV2';
  var PRIMARY_ORDER = ['low_beam', 'headlight_high_low_combined', 'high_beam', 'fog', 'drl'];
  var dataCache = {};
  var productCache = {};
  var recommendationRenderId = 0;
  var selectorStates = [];
  var UI_COPY = {
    resultsTitle: "Your Vehicle's Bulb Sizes",
    trimLabel: 'Trim / Factory Lighting',
    recommendedHeading: 'Recommended Options',
    otherBulbsHeading: 'Exterior & Interior Bulbs',
    otherBulbsIntro: 'These sizes cover additional positions and may vary by trim or factory lighting.',
    referenceGuidance: 'Browse by bulb size. Check the product details and your original bulb before ordering.',
    unknownHeadlightHelp: 'Need help identifying your headlights?',
    referencePackage: 'Factory lighting not identified',
    clearedStatus: 'Vehicle selection cleared.'
  };

  function normalizeIdentityPart(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/^\s+|\s+$/g, '')
      .replace(/\s+/g, ' ');
  }

  function normalizeRulePart(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function vehicleKey(year, make, model) {
    return [Number(year), normalizeIdentityPart(make), normalizeIdentityPart(model)].join('|');
  }

  function isSupportedYear(value, minimum) {
    var year = Number(value);
    var floor = Number(minimum || MIN_MODEL_YEAR);
    return Number.isInteger(year) && year >= floor;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function unique(values) {
    return values.filter(function(value, index, list) {
      return value && list.indexOf(value) === index;
    });
  }

  function sortText(values) {
    return values.sort(function(a, b) {
      return String(a).localeCompare(String(b), 'en', { numeric: true, sensitivity: 'base' });
    });
  }

  function fetchJson(url) {
    if (!dataCache[url]) {
      dataCache[url] = global.fetch(url, { credentials: 'same-origin' }).then(function(response) {
        if (!response.ok) throw new Error('Vehicle finder data could not be loaded.');
        return response.json();
      });
    }
    return dataCache[url];
  }

  function setOptions(select, label, values, getValue, getLabel) {
    select.innerHTML = '';
    var placeholder = global.document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = label;
    select.appendChild(placeholder);
    values.forEach(function(item) {
      var option = global.document.createElement('option');
      option.value = getValue ? getValue(item) : item;
      option.textContent = getLabel ? getLabel(item) : item;
      select.appendChild(option);
    });
    select.disabled = values.length === 0;
    select.value = '';
  }

  function clearSelect(select, label) {
    setOptions(select, label, []);
  }

  function parseShardMap(root) {
    var node = root.querySelector('[data-vehicle-shard-map]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function safeJsonParse(value) {
    try {
      return value ? JSON.parse(value) : null;
    } catch (error) {
      return null;
    }
  }

  function parseCollectionsUrl(value) {
    var parsed = safeJsonParse(value);
    var route = typeof parsed === 'string' ? parsed : String(value || '');
    route = route.replace(/\/+$/g, '');
    if (!route || route.charAt(0) !== '/' || route.indexOf('//') === 0 || /[?#]/.test(route)) return '/collections';
    return route;
  }

  function readStoredSelection(storage) {
    try {
      return storage ? safeJsonParse(storage.getItem(STORAGE_KEY)) : null;
    } catch (error) {
      return null;
    }
  }

  function getFinderStorage(target) {
    try {
      return target && target.localStorage ? target.localStorage : null;
    } catch (error) {
      return null;
    }
  }

  function readRestoredSelection(search, stored, minimum) {
    var saved = stored && typeof stored === 'object' ? stored : {};
    var params;
    try {
      params = new URLSearchParams(search || '');
    } catch (error) {
      params = new URLSearchParams('');
    }
    var selection = {
      year: params.get('year') || params.get('vehicle_year') || saved.year || '',
      make: params.get('make') || params.get('vehicle_make') || saved.make || '',
      model: params.get('model') || params.get('vehicle_model') || saved.model || '',
      configuration: params.get('configuration') || params.get('vehicle_configuration') || saved.configuration || ''
    };
    if (selection.year && !isSupportedYear(selection.year, minimum)) {
      return { selection: { year: '', make: '', model: '', configuration: '' }, unsupportedYear: String(selection.year) };
    }
    return { selection: selection, unsupportedYear: '' };
  }

  function saveSelection(selection, storage) {
    try {
      if (storage) storage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (error) {
      // Storage can be unavailable in privacy modes. The URL remains the portable fallback.
    }
  }

  function clearStoredSelection(storage) {
    try {
      if (storage) storage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Ignore unavailable storage.
    }
  }

  function buildSelectionUrl(selection, href) {
    var url = new URL(href);
    ['year', 'make', 'model', 'configuration'].forEach(function(key) {
      if (selection[key]) url.searchParams.set(key, selection[key]);
      else url.searchParams.delete(key);
    });
    ['vehicle_year', 'vehicle_make', 'vehicle_model', 'vehicle_configuration'].forEach(function(key) {
      url.searchParams.delete(key);
    });
    return url.toString();
  }

  function updateSelectionUrl(selection) {
    if (!global.history || !global.location || typeof global.history.replaceState !== 'function') return;
    try {
      global.history.replaceState(global.history.state, '', buildSelectionUrl(selection, global.location.href));
    } catch (error) {
      // Old browsers can still use the finder without URL persistence.
    }
  }

  function yearEntry(index, value) {
    return (index.years || []).filter(function(entry) {
      return Number(entry.year) === Number(value);
    })[0] || null;
  }

  function makeEntry(index, year, make) {
    var entry = yearEntry(index, year);
    var normalized = normalizeIdentityPart(make);
    return entry ? (entry.makes || []).filter(function(item) {
      return normalizeIdentityPart(item.make) === normalized;
    })[0] || null : null;
  }

  function modelExists(index, year, make, model) {
    var entry = makeEntry(index, year, make);
    var normalized = normalizeIdentityPart(model);
    return !!entry && (entry.models || []).some(function(item) {
      return normalizeIdentityPart(item) === normalized;
    });
  }

  function findVehicle(shard, year, make, model, minimum) {
    if (!isSupportedYear(year, minimum)) return null;
    var key = vehicleKey(year, make, model);
    return (shard.vehicles || []).filter(function(vehicle) {
      return isSupportedYear(vehicle.year, minimum) && vehicleKey(vehicle.year, vehicle.make, vehicle.model) === key;
    })[0] || null;
  }

  function getPositionGroups(configuration) {
    if (!configuration || !configuration.positions) return [];
    return Object.keys(configuration.positions).filter(function(application) {
      return Array.isArray(configuration.positions[application]) && configuration.positions[application].length;
    }).map(function(application) {
      var rows = configuration.positions[application];
      return {
        key: application,
        label: rows[0].label || application.replace(/_/g, ' '),
        rows: rows
      };
    }).sort(function(a, b) {
      var left = PRIMARY_ORDER.indexOf(a.key);
      var right = PRIMARY_ORDER.indexOf(b.key);
      if (left === -1) left = 999;
      if (right === -1) right = 999;
      return left - right || a.label.localeCompare(b.label);
    });
  }

  function isFactoryLedRecord(record) {
    var bulb = normalizeRulePart(record && record.bulb_size);
    var system = normalizeRulePart(record && record.system_type);
    return record && (record.fitment_type === 'factory_led' || system === 'factory_led' || bulb === 'factory_led' || bulb === 'integrated_led' || bulb === 'no_replaceable_bulb');
  }

  function normalizeBulbText(value) {
    return String(value || '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function getBulbTokens(value) {
    return unique(normalizeBulbText(value).split(' ').filter(Boolean));
  }

  function getBulbAliases(bulbSize) {
    var tokens = getBulbTokens(bulbSize);
    var aliases = tokens.slice();
    [['9005', 'HB3'], ['9006', 'HB4'], ['H10', '9140', '9145'], ['H11', 'H8', 'H9']].forEach(function(group) {
      if (group.some(function(alias) { return tokens.indexOf(alias) !== -1; })) aliases = aliases.concat(group);
    });
    return unique(aliases);
  }

  function getVariantFields(variant) {
    var fields = [variant.title, variant.option1, variant.option2, variant.option3];
    if (variant.options && variant.options.length) fields = fields.concat(variant.options);
    return fields.filter(Boolean);
  }

  function fieldHasToken(field, token) {
    return getBulbTokens(field).indexOf(token) !== -1;
  }

  function findVariantByTokens(variants, tokens, getFields) {
    var match = null;
    tokens.some(function(token) {
      match = variants.filter(function(variant) {
        return getFields(variant).some(function(field) { return fieldHasToken(field, token); });
      })[0] || null;
      return !!match;
    });
    return match;
  }

  function findVariantForBulbSize(product, bulbSize) {
    var variants = product && product.variants ? product.variants : [];
    var exact = normalizeBulbText(bulbSize);
    var bulbs = getBulbTokens(bulbSize);
    var aliases = getBulbAliases(bulbSize).filter(function(alias) { return bulbs.indexOf(alias) === -1; });
    var match;
    if (!exact || !variants.length) return null;
    match = variants.filter(function(variant) {
      return getVariantFields(variant).some(function(field) { return normalizeBulbText(field) === exact; });
    })[0];
    if (match) return match;
    match = findVariantByTokens(variants, bulbs, getVariantFields);
    if (match) return match;
    match = findVariantByTokens(variants, aliases, getVariantFields);
    if (match) return match;
    return findVariantByTokens(variants, bulbs, function(variant) { return [variant.title].filter(Boolean); });
  }

  function getProductData(handle) {
    if (!handle) return Promise.reject(new Error('Missing product handle.'));
    if (!productCache[handle]) {
      productCache[handle] = global.fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' }).then(function(response) {
        if (!response.ok) throw new Error('Product data could not be loaded.');
        return response.json();
      });
    }
    return productCache[handle];
  }

  function getProductImage(product) {
    if (!product) return '';
    return product.featured_image || (product.images && product.images[0]) || '';
  }

  function getProductUrl(handle, variant) {
    var url = '/products/' + encodeURIComponent(handle);
    if (variant && variant.id) url += '?variant=' + encodeURIComponent(variant.id);
    return url;
  }

  function readableHandle(handle) {
    return String(handle || 'Contact BRI Source').replace(/-/g, ' ').replace(/\b\w/g, function(character) { return character.toUpperCase(); });
  }

  function getBulbCollectionHandle(bulbSize) {
    var value = String(bulbSize || '').trim();
    var key = normalizeRulePart(value);
    var dSeries = getBulbTokens(value).filter(function(token) { return /^D[1-8][RS]$/.test(token); })[0];
    var handles = {
      h1: 'h1-headlight-foglight-bulbs', h3: 'h3-headlight-foglight-bulbs', h4: 'h4-headlight-bulbs', h7: 'h7-headlight-bulbs',
      h8: 'h8-foglight-bulbs', h9: 'h9-headlight-bulbs', h10: 'h10-foglight-bulbs', h10_9145: 'h10-foglight-bulbs', h11: 'h11-headlight-bulbs',
      h13: 'h13-headlight-bulbs', h15: 'h15-headlight-bulbs', h16: 'h16-headlight-bulbs', '9003': '9003-headlight-bulbs',
      '9003_h4': '9003-headlight-bulbs', '9004': '9004-headlight-bulbs', '9005': '9005-headlight-bulbs', '9005_hb3': '9005-headlight-bulbs',
      '9006': '9006-headlight-foglight-bulbs', '9006_hb4': '9006-headlight-foglight-bulbs', '9007': '9007-headlight-bulbs', '9008': 'h13-headlight-bulbs',
      '9008_h13': 'h13-headlight-bulbs', '9011': '9011-headlight-bulbs', '9012': '9012-headlight-bulbs', '9012_hir2': '9012-headlight-bulbs',
      '880': '880-foglight-bulbs', '800': '880-foglight-bulbs', '5202': '5202-foglight-bulbs', '2504': '5202-foglight-bulbs', '9145': '9145-fog-light-bulbs',
      d1s: 'd1s-headlight-bulbs', d2s: 'd2s-headlight-bulbs', d3s: 'd3s-headlight-bulbs', d4s: 'd4s-headlight-bulbs', d5s: 'd5s-headlight-bulbs',
      '1156': '1156-bulbs', '1157': '1157-bulbs', '3156': '3156-bulbs', '3157': '3157-bulbs', '7440': '7440-bulbs', '7443': '7443-bulbs',
      '194_168_t10': '194-168-t10-bulbs', '921_912_t15': '921-912-t15-bulbs'
    };
    if (!value || ['not_listed', 'factory_led', 'integrated_led', 'confirm_with_bri_source', 'unknown_confirm_installed_bulb'].indexOf(key) !== -1) return '';
    if (dSeries) return 'd' + dSeries.charAt(1).toLowerCase() + 's-headlight-bulbs';
    return handles[key] || '';
  }

  function isStandaloneBulbCode(value) {
    var code = String(value || '').trim();
    var generic = [
      'unknown', 'n/a', 'na', 'not applicable', 'not listed', 'assembly', 'headlight assembly',
      'tail light assembly', 'integrated led', 'factory led', 'led', 'hid', 'halogen', 'xenon',
      'confirm with bri source', 'unknown confirm installed bulb', 'no replaceable bulb',
      'not separately serviceable', 'sealed beam'
    ];
    if (!code || code.length > 32 || generic.indexOf(code.toLowerCase().replace(/[—–]/g, '-').replace(/\s+/g, ' ')) !== -1) return false;
    return /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/.test(code) && /[0-9]/.test(code);
  }

  function bulbCodeHandle(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\u2018\u2019']/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function explicitBulbCollectionHandle(record, code) {
    var handles = record && record.collection_handles;
    var handle = handles && (handles[code] || handles[String(code).toUpperCase()] || handles[String(code).toLowerCase()]);
    if (!handle && record && record.collection_handle && String(record.bulb_size || '').trim() === String(code).trim()) handle = record.collection_handle;
    handle = String(handle || '').trim();
    return /^[a-z0-9][a-z0-9-]*$/.test(handle) ? handle : '';
  }

  function bulbCollectionPath(code, collectionsUrl, record) {
    if (!isStandaloneBulbCode(code)) return '';
    var handle = explicitBulbCollectionHandle(record, code) || bulbCodeHandle(code) + '-bulbs';
    return parseCollectionsUrl(collectionsUrl) + '/' + encodeURIComponent(handle);
  }

  function renderBulbCodeLink(code, record, collectionsUrl) {
    var path = bulbCollectionPath(code, collectionsUrl, record);
    if (!path) return escapeHtml(code);
    return '<a class="vehicle-selector-placeholder__bulb-link" href="' + escapeHtml(path) + '" aria-label="Shop ' + escapeHtml(code) + ' bulbs">' + escapeHtml(code) + '</a>';
  }

  function renderBulbSizeLinks(value, record, collectionsUrl) {
    var text = String(value || '').trim();
    var separator = /^\s*(?:\/|,|\||\+|&|or|\(|\))\s*$/i;
    var parts;
    var codeCount = 0;
    if (!text || (record && (record.fitment_type !== 'replaceable_bulb' || isFactoryLedRecord(record)))) return escapeHtml(text || 'Unknown');
    if (isStandaloneBulbCode(text)) return renderBulbCodeLink(text, record, collectionsUrl);
    parts = text.split(/(\s*(?:\/|,|\||\+|&|\bor\b|\(|\))\s*)/i);
    if (parts.length < 3) return escapeHtml(text);
    var rendered = parts.map(function(part) {
      var match;
      if (!part) return '';
      if (separator.test(part)) return escapeHtml(part);
      match = part.match(/^(\s*)([A-Za-z0-9]+(?:-[A-Za-z0-9]+)*)(\s*)$/);
      if (!match || !isStandaloneBulbCode(match[2])) return null;
      codeCount += 1;
      return escapeHtml(match[1]) + renderBulbCodeLink(match[2], record, collectionsUrl) + escapeHtml(match[3]);
    });
    if (rendered.some(function(part) { return part === null; }) || codeCount < 2) return escapeHtml(text);
    return rendered.join('');
  }

  function isDSeriesBulb(value) {
    return getBulbTokens(value).some(function(token) { return /^D[1-8][RS]$/.test(token); });
  }

  function recommendationPositionKey(application) {
    if (application === 'headlight_high_low_combined') return 'low_beam';
    return application;
  }

  function isRecommendationEligible(record) {
    if (!record || record.recommendation_allowed !== true || record.fitment_type !== 'replaceable_bulb' || isFactoryLedRecord(record)) return false;
    var bulb = String(record.bulb_size || '').trim();
    return !!bulb && !/\s+OR\s+/i.test(bulb) && (!!getBulbCollectionHandle(bulb) || isDSeriesBulb(bulb));
  }

  function getRecommendationRuleKeys(record, application) {
    var keys = [];
    if (!isRecommendationEligible(record)) return keys;
    var position = recommendationPositionKey(application);
    var system = normalizeRulePart(record.system_type || 'halogen');
    var housing = normalizeRulePart(record.housing_type || '');
    if (system === 'oe_xenon' || isDSeriesBulb(record.bulb_size)) keys.push('oe_xenon_' + position);
    else if (position === 'fog') keys.push(system + '_fog');
    else if (housing) keys.push(system + '_' + housing + '_' + position);
    keys.push('replaceable_' + position);
    return unique(keys);
  }

  function getRecommendations(record, application, rulesData) {
    var rules = rulesData && rulesData.rules ? rulesData.rules : {};
    if (record.recommendation_overrides && record.recommendation_overrides.length) return record.recommendation_overrides.slice();
    var result = [];
    getRecommendationRuleKeys(record, application).some(function(key) {
      if (rules[key]) { result = rules[key]; return true; }
      return false;
    });
    return result.slice().sort(function(a, b) { return (a.priority || 999) - (b.priority || 999); });
  }

  function renderBulbValue(record, collectionsUrl) {
    var value = record.bulb_size || record.canonical_bulb || 'Unknown';
    return renderBulbSizeLinks(value, record, collectionsUrl);
  }

  function renderSummary(container, groups, collectionsUrl) {
    if (!groups.length) {
      container.innerHTML = '<div><dt>Status</dt><dd>No bulb size is available for this selection.</dd></div>';
      return;
    }
    container.innerHTML = groups.map(function(group) {
      return '<div><dt>' + escapeHtml(group.label) + '</dt><dd>' + group.rows.map(function(record) { return renderBulbValue(record, collectionsUrl); }).join('<span aria-hidden="true"> · </span>') + '</dd></div>';
    }).join('');
  }

  function renderTabs(container, groups, activeKey) {
    container.setAttribute('role', 'tablist');
    container.innerHTML = groups.map(function(group, index) {
      var active = group.key === activeKey;
      return '<button type="button" role="tab" class="vehicle-selector-placeholder__tab' + (active ? ' vehicle-selector-placeholder__tab--active' : '') + '" data-position-key="' + escapeHtml(group.key) + '" aria-selected="' + (active ? 'true' : 'false') + '" tabindex="' + (active ? '0' : '-1') + '">' + escapeHtml(group.label) + '</button>';
    }).join('');
  }

  function setActiveTab(container, key) {
    Array.prototype.forEach.call(container.querySelectorAll('[data-position-key]'), function(button) {
      var active = button.getAttribute('data-position-key') === key;
      button.classList.toggle('vehicle-selector-placeholder__tab--active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
  }

  function renderRecordDisclosure(record) {
    if (record.fitment_type === 'unknown') return '<p class="vehicle-selector-placeholder__source-disclosure">A replacement bulb size is not specified for this factory lighting setup. ' + UI_COPY.unknownHeadlightHelp + '</p>';
    return '';
  }

  function recordApplicability(record) {
    return unique((record && record.applicability || []).concat(record && record.qualifiers || []));
  }

  function renderApplicabilityLine(record) {
    var values = recordApplicability(record);
    return values.length ? '<p class="vehicle-selector-placeholder__record-applicability"><strong>Applies to:</strong> ' + escapeHtml(values.join('; ')) + '</p>' : '';
  }

  function renderRecommendationApplicabilityLine(record) {
    if (!record || !Array.isArray(record.recommendation_applicability)) return renderApplicabilityLine(record);
    var scopedRecord = Object.assign({}, record, {
      applicability: record.recommendation_applicability,
      qualifiers: []
    });
    return renderApplicabilityLine(scopedRecord);
  }

  function renderInformationalRows(container, group, collectionsUrl) {
    var hasBrowsableSize = group.rows.some(function(record) {
      return renderBulbValue(record, collectionsUrl).indexOf('<a ') !== -1;
    });
    container.innerHTML = '<article class="vehicle-selector-placeholder__bulb-information">' +
      (hasBrowsableSize ? '<p class="vehicle-selector-placeholder__reference-note">' + UI_COPY.referenceGuidance + '</p>' : '') + group.rows.map(function(record) {
      return '<div class="vehicle-selector-placeholder__bulb-information-row"><h4>' + renderBulbValue(record, collectionsUrl) + '</h4>' +
        renderApplicabilityLine(record) +
        renderRecordDisclosure(record) + '</div>';
    }).join('') + '</article>';
  }

  function renderProductRecommendations(container, record, group, rulesData, collectionsUrl) {
    var recommendations = getRecommendations(record, group.key, rulesData);
    if (!recommendations.length) {
      renderInformationalRows(container, group, collectionsUrl);
      return;
    }
    var renderId = String(++recommendationRenderId);
    container.dataset.recommendationRenderId = renderId;
    container.innerHTML = '<h4 class="vehicle-selector-placeholder__recommendation-heading">' + UI_COPY.recommendedHeading + '</h4>' + renderRecommendationApplicabilityLine(record) + recommendations.map(function(item) {
      return '<article class="vehicle-selector-placeholder__card vehicle-selector-placeholder__card--loading" data-product-handle="' + escapeHtml(item.handle) + '"><div class="vehicle-selector-placeholder__product-media vehicle-selector-placeholder__product-media--loading" aria-hidden="true"></div><div class="vehicle-selector-placeholder__product-info"><p class="vehicle-selector-placeholder__card-label">' + escapeHtml(item.label) + '</p><h4 class="vehicle-selector-placeholder__product">' + escapeHtml(readableHandle(item.handle)) + '</h4><p>' + escapeHtml(item.reason || '') + '</p><a class="btn btn--secondary vehicle-selector-placeholder__card-button" href="' + escapeHtml(getProductUrl(item.handle)) + '">View product</a></div></article>';
    }).join('');
    recommendations.forEach(function(item) {
      getProductData(item.handle).then(function(product) {
        var card = container.querySelector('[data-product-handle="' + item.handle + '"]');
        if (!card || container.dataset.recommendationRenderId !== renderId) return;
        var variant = findVariantForBulbSize(product, record.bulb_size);
        var image = getProductImage(product);
        card.classList.remove('vehicle-selector-placeholder__card--loading');
        card.innerHTML = (image ? '<div class="vehicle-selector-placeholder__product-media"><img class="vehicle-selector-placeholder__product-image" src="' + escapeHtml(image) + '" alt="' + escapeHtml(product.title || readableHandle(item.handle)) + '" loading="lazy"></div>' : '<div class="vehicle-selector-placeholder__product-media vehicle-selector-placeholder__product-media--empty" aria-hidden="true"></div>') +
          '<div class="vehicle-selector-placeholder__product-info"><p class="vehicle-selector-placeholder__card-label">' + escapeHtml(item.label) + '</p><h4 class="vehicle-selector-placeholder__product">' + escapeHtml(product.title || readableHandle(item.handle)) + '</h4><p>' + escapeHtml(item.reason || '') + '</p>' +
          (!variant && product.variants && product.variants.length > 1 ? '<p class="vehicle-selector-placeholder__variant-note">Select ' + renderBulbValue(record, collectionsUrl) + ' on the product page.</p>' : '') +
          '<a class="btn btn--secondary vehicle-selector-placeholder__card-button" href="' + escapeHtml(getProductUrl(item.handle, variant)) + '">View product</a></div>';
      }).catch(function() {
        var card = container.querySelector('[data-product-handle="' + item.handle + '"]');
        if (card) card.classList.remove('vehicle-selector-placeholder__card--loading');
      });
    });
  }

  function renderRecommendations(container, group, rulesData, collectionsUrl) {
    recommendationRenderId += 1;
    delete container.dataset.recommendationRenderId;
    if (!group || !group.rows.length) {
      container.innerHTML = '<p class="vehicle-selector-placeholder__empty">No recommendation is available for this position.</p>';
      return;
    }
    if (group.rows.some(isFactoryLedRecord)) {
      container.innerHTML = '<article class="vehicle-selector-placeholder__factory-led"><p class="vehicle-selector-placeholder__factory-led-heading">Factory LED lighting</p><h4>No standard replacement bulb</h4><p>This configuration uses an integrated factory LED beam. No conventional replacement-bulb recommendation applies.</p></article>';
      return;
    }
    if (group.rows.length !== 1 || !isRecommendationEligible(group.rows[0])) {
      renderInformationalRows(container, group, collectionsUrl);
      return;
    }
    renderProductRecommendations(container, group.rows[0], group, rulesData, collectionsUrl);
  }

  function conditionText(config) {
    var values = [];
    if (config.scope_type === 'source_unqualified') values.push('Factory package not identified for these reference sizes.');
    Object.keys(config.positions || {}).forEach(function(application) {
      (config.positions[application] || []).forEach(function(record) {
        values = values.concat(record.qualifiers || []);
        var semantics = record.semantics || {};
        values = values.concat(semantics.negative_exclusions || []);
        values = values.concat((semantics.excluded_factory_technology || []).map(function(value) { return 'Excludes factory technology: ' + value; }));
        values = values.concat((semantics.excluded_optics || []).map(function(value) { return 'Excludes optics: ' + value; }));
      });
    });
    return unique(values);
  }

  function configurationRows(configuration) {
    var rows = [];
    Object.keys(configuration && configuration.positions || {}).forEach(function(application) {
      (configuration.positions[application] || []).forEach(function(record) {
        rows.push({ application: application, record: record });
      });
    });
    return rows;
  }

  function isGenericConfigurationLabel(configuration) {
    var label = normalizeRulePart(configuration && configuration.label);
    return ['confirmed_fitment', 'likely_fitment', 'possible_fitment', 'needs_confirmation'].indexOf(label) !== -1;
  }

  function customerConfigurationLabel(configuration) {
    var label = String(configuration && configuration.label || '').trim();
    return label.replace(/\s*(?:,|·|—|-)\s*(?:confirmed fitment|likely fitment|possible fitment|needs confirmation)\s*$/i, '').trim();
  }

  var HEADLIGHT_APPLICATIONS = ['low_beam', 'high_beam', 'headlight_high_low_combined'];

  function normalizedBulbFact(value) {
    var aliases = { HB3: '9005', HB4: '9006', H4: '9003', H13: '9008', HIR2: '9012', '9140': 'H10', '9145': 'H10' };
    return unique(getBulbTokens(value).map(function(token) { return aliases[token] || token; })).sort().join('|');
  }

  function stableDisplayGroupId(values) {
    var input = values.slice().sort().join('|');
    var first = 2166136261;
    var second = 3339675911;
    var index;
    for (index = 0; index < input.length; index += 1) {
      first ^= input.charCodeAt(index);
      first = Math.imul(first, 16777619);
      second ^= input.charCodeAt(index) + index;
      second = Math.imul(second, 2246822519);
    }
    return ('00000000' + (first >>> 0).toString(16)).slice(-8) + ('00000000' + (second >>> 0).toString(16)).slice(-8);
  }

  function configurationTechnology(configuration) {
    var asserted = [];
    configurationRows(configuration).forEach(function(item) {
      if (HEADLIGHT_APPLICATIONS.indexOf(item.application) === -1) return;
      var system = normalizeRulePart(item.record.system_type || '');
      var fitment = normalizeRulePart(item.record.fitment_type || '');
      var bulb = normalizeRulePart(item.record.bulb_size || item.record.canonical_bulb || '');
      if (system === 'factory_led' || fitment === 'factory_led') asserted.push('led');
      else if (system === 'halogen') asserted.push('halogen');
      else if (system === 'oe_xenon' || system === 'hid' || /^d[1-8][rs]$/.test(bulb)) asserted.push('hid');
    });
    var label = String(configuration && configuration.label || '')
      .replace(/\b(?:without|w\/o|except|excluding|excl\.?)\b[^,;·]*/gi, ' ');
    var text = normalizeRulePart(label);
    if (/factory_led|integrated_led|(?:^|_)led(?:_|$)/.test(text)) return 'led';
    if (/factory_(?:hid|xenon)|(?:^|_)(?:hid|xenon)(?:_|$)/.test(text)) return 'hid';
    if (/factory_halogen|(?:^|_)halogen(?:_|$)/.test(text)) return 'halogen';
    asserted = unique(asserted);
    if (asserted.length === 1) return asserted[0];
    if (asserted.indexOf('led') !== -1) return 'led';
    if (asserted.indexOf('hid') !== -1) return 'hid';
    if (asserted.indexOf('halogen') !== -1) return 'halogen';
    var conventionalRows = configurationRows(configuration).filter(function(item) {
      if (HEADLIGHT_APPLICATIONS.indexOf(item.application) === -1) return false;
      var record = item.record || {};
      var bulb = normalizeRulePart(record.bulb_size || record.canonical_bulb || '');
      return record.fitment_type === 'replaceable_bulb' && bulb && bulb.indexOf('confirm_installed') === -1;
    });
    if (conventionalRows.length && conventionalRows.length === configurationRows(configuration).filter(function(item) {
      return HEADLIGHT_APPLICATIONS.indexOf(item.application) !== -1;
    }).length) return 'halogen';
    return '';
  }

  function configurationOptics(configuration) {
    var values = [String(configuration && configuration.label || '').replace(/\b(?:without|w\/o|except|excluding|excl\.?)\b[^,;·]*/gi, ' ')];
    configurationRows(configuration).forEach(function(item) {
      if (HEADLIGHT_APPLICATIONS.indexOf(item.application) !== -1) values.push(item.record.housing_type || '');
    });
    var text = normalizeRulePart(values.join(' '));
    var projector = /(?:^|_)projector(?:_|$)/.test(text);
    var reflector = /(?:^|_)reflector(?:_|$)/.test(text);
    return projector && !reflector ? 'projector' : reflector && !projector ? 'reflector' : '';
  }

  function configurationTechnologyEvidence(configuration) {
    var label = normalizeRulePart(String(configuration && configuration.label || '')
      .replace(/\b(?:without|w\/o|except|excluding|excl\.?)\b[^,;·]*/gi, ' '));
    if (/(?:^|_)(?:halogen|hid|xenon|led)(?:_|$)/.test(label)) return 'explicit';
    var explicitRows = configurationRows(configuration).some(function(item) {
      if (HEADLIGHT_APPLICATIONS.indexOf(item.application) === -1) return false;
      var system = normalizeRulePart(item.record.system_type || '');
      var fitment = normalizeRulePart(item.record.fitment_type || '');
      var bulb = normalizeRulePart(item.record.bulb_size || item.record.canonical_bulb || '');
      return system === 'halogen' || system === 'factory_led' || system === 'oe_xenon' || system === 'hid' || fitment === 'factory_led' || /^d[1-8][rs]$/.test(bulb);
    });
    return explicitRows ? 'explicit' : 'inferred';
  }

  function hasHeadlightRows(configuration) {
    return HEADLIGHT_APPLICATIONS.some(function(application) {
      return configuration && configuration.positions && Array.isArray(configuration.positions[application]) && configuration.positions[application].length;
    });
  }

  function recordFact(record) {
    var semantics = record && record.semantics || {};
    var warnings = unique([].concat(
      semantics.negative_exclusions || [],
      semantics.excluded_factory_technology || [],
      semantics.excluded_optics || []
    )).sort();
    var overrides = (record && record.recommendation_overrides || []).map(function(value) {
      return typeof value === 'string' ? value : JSON.stringify(value);
    }).sort();
    return [
      record && record.fitment_type || '',
      normalizedBulbFact(record && (record.bulb_size || record.canonical_bulb)),
      normalizeRulePart(record && record.system_type),
      normalizeRulePart(record && record.housing_type),
      record && record.recommendation_allowed === true ? 'recommended' : 'reference',
      overrides.join('|'),
      record && record.source_listed_unverified === true ? 'unverified' : '',
      warnings.join('|')
    ].join(':');
  }

  function applicationFacts(configuration, application) {
    return unique((configuration && configuration.positions && configuration.positions[application] || []).map(recordFact)).sort();
  }

  function primarySignature(configuration) {
    return HEADLIGHT_APPLICATIONS.map(function(application) {
      return application + '=' + applicationFacts(configuration, application).join(',');
    }).join(';');
  }

  function customerResultSignature(configuration) {
    return Object.keys(configuration && configuration.positions || {}).sort().map(function(application) {
      return application + '=' + applicationFacts(configuration, application).join(',');
    }).join(';');
  }

  function primaryConfigurationIsComplete(configuration) {
    var combined = applicationFacts(configuration, 'headlight_high_low_combined').length > 0;
    var separate = applicationFacts(configuration, 'low_beam').length > 0 && applicationFacts(configuration, 'high_beam').length > 0;
    return combined || separate;
  }

  function primaryConfigurationsCompatible(left, right) {
    var leftCombined = applicationFacts(left, 'headlight_high_low_combined').length > 0;
    var rightCombined = applicationFacts(right, 'headlight_high_low_combined').length > 0;
    var leftSeparate = applicationFacts(left, 'low_beam').length > 0 || applicationFacts(left, 'high_beam').length > 0;
    var rightSeparate = applicationFacts(right, 'low_beam').length > 0 || applicationFacts(right, 'high_beam').length > 0;
    if ((leftCombined && rightSeparate) || (rightCombined && leftSeparate)) return false;
    return HEADLIGHT_APPLICATIONS.every(function(application) {
      var leftFacts = applicationFacts(left, application);
      var rightFacts = applicationFacts(right, application);
      return !leftFacts.length || !rightFacts.length || JSON.stringify(leftFacts) === JSON.stringify(rightFacts);
    });
  }

  function cleanApplicabilityLabel(configuration) {
    var original = customerConfigurationLabel(configuration);
    var label = original
      .replace(/\s*[·,]\s*(?:factory\s+)?(?:halogen|hid(?:\s*\/\s*xenon)?|xenon|led)\s+(?:projector|reflector|headlights?).*$/i, '')
      .replace(/\s*,?\s*factory\s+(?:halogen|hid(?:\s*\/\s*xenon)?|xenon|led)\s+headlights?\s*$/i, '')
      .replace(/^w\/(?:halogen|hid)\s+h\/l$/i, '')
      .replace(/^projector\s+headlights?$/i, '')
      .trim().replace(/[·,;\-]+$/g, '').trim();
    var qualifiers = unique(configurationRows(configuration).reduce(function(values, item) {
      return values.concat(item.record.qualifiers || []);
    }, []));
    function normalizeCustomerCondition(value) {
      var bodyLabels = {
        coupe: 'Coupe',
        sedan: 'Sedan',
        hatchback: 'Hatchback',
        wagon: 'Wagon',
        convertible: 'Convertible',
        cabriolet: 'Convertible',
        roadster: 'Roadster',
        liftback: 'Liftback',
        fastback: 'Fastback',
        pickup: 'Pickup',
        suv: 'SUV',
        van: 'Van',
        minivan: 'Minivan'
      };
      var output = String(value || '').replace(/\bbody\s+style\s*:\s*(coupe|sedan|hatchback|wagon|convertible|cabriolet|roadster|liftback|fastback|pickup|suv|van|minivan)\b/gi, function(match, body) {
        return bodyLabels[body.toLowerCase()] || body;
      }).replace(/\b(coupe|sedan|hatchback|wagon|convertible|cabriolet|roadster|liftback|fastback|pickup|suv|van|minivan)\s+body\b/gi, function(match, body) {
        return bodyLabels[body.toLowerCase()] || body;
      });
      return output.replace(/\s+/g, ' ').trim().replace(/^[·,;\-\s]+|[·,;\-\s]+$/g, '');
    }
    if (configuration && configuration.source_type === 'factory_reference' && qualifiers.length) return normalizeCustomerCondition(qualifiers[0]);
    if (/^w\/halogen\s+h\/l$/i.test(original)) return 'Other configurations with halogen headlights';
    if (/^w\/(?:hid|xenon)\s+h\/l$/i.test(original)) return 'Other configurations with factory HID / Xenon headlights';
    if (/^factory\s+halogen\s+headlights?$/i.test(original)) return 'Factory-halogen configuration; trim/package not specified';
    if (/^factory\s+(?:hid\s*\/\s*xenon|hid|xenon)\s+headlights?$/i.test(original)) return 'Factory HID / Xenon configuration; trim/package not specified';
    if (/^(?:factory\s+)?(?:halogen|hid|xenon|led)\s+(?:projector|reflector|headlights?)$/i.test(label)) return '';
    return normalizeCustomerCondition(label);
  }

  function seedConfiguration(configuration) {
    if (!configuration || configuration.source_type === 'public_reference' || isGenericConfigurationLabel(configuration) || !hasHeadlightRows(configuration)) return false;
    if (configuration.source_type === 'factory_reference' && !configurationTechnology(configuration)) return false;
    return true;
  }

  function sourceFamily(configuration) {
    return configuration && configuration.source_type === 'factory_reference' ? 'factory' : 'existing';
  }

  function addMemberToGroup(group, configuration) {
    group.members.push(configuration);
    group.legacyKeys.push(configuration.key);
    if (group.technologyEvidence && group.technologyEvidence !== configurationTechnologyEvidence(configuration)) group.technologyEvidence = 'mixed';
    var applicability = cleanApplicabilityLabel(configuration);
    if (applicability) group.applicability.push(applicability);
  }

  function mergeGroupRecord(group, application, sourceRecord, applicability, resolution, equivalentDisplayRecord) {
    if (!group.positions[application]) group.positions[application] = [];
    var fact = recordFact(sourceRecord);
    var existing = group.positions[application].filter(function(record) { return recordFact(record) === fact; })[0];
    if (!existing && equivalentDisplayRecord) {
      existing = group.positions[application].filter(function(record) {
        return record.fitment_type === sourceRecord.fitment_type && normalizedBulbFact(record.bulb_size || record.canonical_bulb) === normalizedBulbFact(sourceRecord.bulb_size || sourceRecord.canonical_bulb);
      })[0];
    }
    var values = applicability ? [applicability] : [];
    if (!existing) {
      existing = Object.assign({}, sourceRecord);
      existing.qualifiers = unique((sourceRecord.qualifiers || []).slice());
      existing.applicability = unique(values.concat(sourceRecord.applicability || []));
      existing.recommendation_applicability = sourceRecord.recommendation_allowed === true ? unique((sourceRecord.recommendation_applicability || []).concat(sourceRecord.applicability || []).concat(values)) : [];
      existing.resolution = resolution || sourceRecord.resolution || '';
      group.positions[application].push(existing);
      return existing;
    }
    existing.qualifiers = unique((existing.qualifiers || []).concat(sourceRecord.qualifiers || []));
    existing.applicability = unique((existing.applicability || []).concat(values).concat(sourceRecord.applicability || []));
    existing.upstream_ids = unique((existing.upstream_ids || []).concat(sourceRecord.upstream_ids || []).concat(sourceRecord.id || []));
    if (!existing.housing_type && sourceRecord.housing_type) existing.housing_type = sourceRecord.housing_type;
    if (!existing.system_type && sourceRecord.system_type) existing.system_type = sourceRecord.system_type;
    if (sourceRecord.recommendation_allowed === true) {
      existing.recommendation_applicability = unique((existing.recommendation_applicability || []).concat(sourceRecord.recommendation_applicability || []).concat(sourceRecord.applicability || []).concat(values));
    }
    existing.recommendation_allowed = existing.recommendation_allowed === true || sourceRecord.recommendation_allowed === true;
    return existing;
  }

  function createDisplayGroup(configuration) {
    var group = {
      members: [],
      legacyKeys: [],
      applicability: [],
      positions: {},
      technology: configurationTechnology(configuration) || 'unknown',
      technologyEvidence: configurationTechnologyEvidence(configuration),
      optics: configurationOptics(configuration) || 'unknown',
      family: sourceFamily(configuration)
    };
    addMemberToGroup(group, configuration);
    return group;
  }

  function groupSeedConfigurations(configurations) {
    var groups = [];
    configurations.forEach(function(configuration) {
      var match = groups.filter(function(group) {
        return group.family === sourceFamily(configuration) &&
          group.technology === (configurationTechnology(configuration) || 'unknown') &&
          group.optics === (configurationOptics(configuration) || 'unknown') &&
          customerResultSignature(group.members[0]) === customerResultSignature(configuration);
      })[0];
      if (match) addMemberToGroup(match, configuration);
      else groups.push(createDisplayGroup(configuration));
    });
    groups.forEach(function(group) {
      group.members.forEach(function(configuration) {
        var applicability = cleanApplicabilityLabel(configuration);
        Object.keys(configuration.positions || {}).forEach(function(application) {
          (configuration.positions[application] || []).forEach(function(record) {
            mergeGroupRecord(group, application, record, applicability, 'retained-source-record');
          });
        });
      });
      group.applicability = unique(group.applicability);
    });
    return groups;
  }

  function genericApplicability(value) {
    return /^other configurations with\b/i.test(value) || /trim\/package not specified|factory package not identified/i.test(value);
  }

  function applicabilityTokens(group) {
    return unique((group.applicability || []).reduce(function(values, value) {
      if (genericApplicability(value)) return values;
      return values.concat(String(value || '').split(/[|;,]/).map(function(part) { return normalizeRulePart(part); }).filter(Boolean));
    }, []));
  }

  function complementaryApplicability(left, right) {
    var leftValues = left.applicability || [];
    var rightValues = right.applicability || [];
    var leftGeneric = leftValues.length && leftValues.every(genericApplicability);
    var rightGeneric = rightValues.length && rightValues.every(genericApplicability);
    if (leftGeneric && rightGeneric) return true;
    var leftTokens = applicabilityTokens(left);
    var rightTokens = applicabilityTokens(right);
    return leftTokens.some(function(value) { return rightTokens.indexOf(value) !== -1; });
  }

  function complementaryRecordFact(record) {
    var semantics = record && record.semantics || {};
    var warnings = unique([].concat(
      semantics.negative_exclusions || [],
      semantics.excluded_factory_technology || [],
      semantics.excluded_optics || []
    )).sort();
    var overrides = (record && record.recommendation_overrides || []).map(function(value) {
      return typeof value === 'string' ? value : JSON.stringify(value);
    }).sort();
    return [
      record && record.fitment_type || '',
      normalizedBulbFact(record && (record.bulb_size || record.canonical_bulb)),
      record && record.recommendation_allowed === true ? 'recommended' : 'reference',
      overrides.join('|'),
      record && record.source_listed_unverified === true ? 'unverified' : '',
      warnings.join('|')
    ].join(':');
  }

  function complementaryResults(left, right) {
    var shared = Object.keys(left.positions || {}).filter(function(application) {
      return (left.positions[application] || []).length && (right.positions[application] || []).length;
    });
    return shared.every(function(application) {
      var leftFacts = unique((left.positions[application] || []).map(complementaryRecordFact)).sort();
      var rightFacts = unique((right.positions[application] || []).map(complementaryRecordFact)).sort();
      return JSON.stringify(leftFacts) === JSON.stringify(rightFacts);
    });
  }

  function groupHasGenericApplicability(group) {
    return (group.applicability || []).some(genericApplicability);
  }

  function groupPrimaryResultsCompatible(left, right) {
    var leftCombined = groupApplicationBulbSignature(left, 'headlight_high_low_combined');
    var rightCombined = groupApplicationBulbSignature(right, 'headlight_high_low_combined');
    var leftSeparate = groupApplicationBulbSignature(left, 'low_beam') || groupApplicationBulbSignature(left, 'high_beam');
    var rightSeparate = groupApplicationBulbSignature(right, 'low_beam') || groupApplicationBulbSignature(right, 'high_beam');
    if ((leftCombined && rightSeparate) || (rightCombined && leftSeparate)) return false;
    return HEADLIGHT_APPLICATIONS.every(function(application) {
      var leftValue = groupApplicationBulbSignature(left, application);
      var rightValue = groupApplicationBulbSignature(right, application);
      if (!leftValue || !rightValue) return true;
      if (leftValue !== rightValue) return false;
      var leftFacts = unique((left.positions[application] || []).map(complementaryRecordFact)).sort();
      var rightFacts = unique((right.positions[application] || []).map(complementaryRecordFact)).sort();
      return JSON.stringify(leftFacts) === JSON.stringify(rightFacts);
    });
  }

  function mergeGroupContents(target, source, equivalentDisplayRecord) {
    source.members.forEach(function(member) { if (target.members.indexOf(member) === -1) target.members.push(member); });
    target.legacyKeys = unique(target.legacyKeys.concat(source.legacyKeys));
    target.applicability = unique(target.applicability.concat(source.applicability));
    target.referenceAliases = unique((target.referenceAliases || []).concat(source.referenceAliases || []));
    target.resolvedUnknowns = (target.resolvedUnknowns || 0) + (source.resolvedUnknowns || 0);
    if (target.optics === 'unknown' && source.optics !== 'unknown') target.optics = source.optics;
    Object.keys(source.positions).forEach(function(application) {
      source.positions[application].forEach(function(record) {
        mergeGroupRecord(target, application, record, '', record.resolution || 'retained-source-record', equivalentDisplayRecord === true);
      });
    });
  }

  function mergeComplementaryGroups(groups) {
    var output = groups.slice();
    var changed = true;
    while (changed) {
      changed = false;
      for (var leftIndex = 0; leftIndex < output.length && !changed; leftIndex += 1) {
        for (var rightIndex = leftIndex + 1; rightIndex < output.length; rightIndex += 1) {
          var left = output[leftIndex];
          var right = output[rightIndex];
          var opticsCompatible = left.optics === right.optics || left.optics === 'unknown' || right.optics === 'unknown';
          var sameCompletePrimary = groupPrimaryBulbSignature(left) === groupPrimaryBulbSignature(right);
          var complementary = left.family === right.family &&
            left.technology === right.technology &&
            opticsCompatible &&
            groupPrimaryResultsCompatible(left, right) &&
            (complementaryApplicability(left, right) || groupHasGenericApplicability(left) || groupHasGenericApplicability(right)) &&
            (complementaryResults(left, right) || (
              left.technologyEvidence === 'explicit' &&
              right.technologyEvidence === 'explicit' &&
              left.optics === right.optics &&
              (sameCompletePrimary || groupHasGenericApplicability(left) || groupHasGenericApplicability(right))
            ));
          if (!complementary) continue;
          mergeGroupContents(left, right, false);
          output.splice(rightIndex, 1);
          changed = true;
          break;
        }
      }
    }
    return output;
  }

  function broadTechnologyScope(configuration) {
    var match = String(configuration && configuration.scope_key || '').match(/^structured:lighting_technology=(halogen|hid|led)$/i);
    return match ? match[1].toLowerCase() : '';
  }

  function coherentBroadEvidence(configuration) {
    var technology = broadTechnologyScope(configuration);
    if (!technology) return null;
    var positions = configuration.positions || {};
    var combined = (positions.headlight_high_low_combined || []).length > 0;
    var separate = (positions.low_beam || []).length > 0 || (positions.high_beam || []).length > 0;
    if (combined && separate) return null;
    var rows = [];
    var coherent = HEADLIGHT_APPLICATIONS.every(function(application) {
      var known = (positions[application] || []).filter(function(record) {
        return record.fitment_type === 'replaceable_bulb' && normalizedBulbFact(record.bulb_size || record.canonical_bulb);
      });
      var bulbs = unique(known.map(function(record) { return normalizedBulbFact(record.bulb_size || record.canonical_bulb); }));
      if (bulbs.length > 1) return false;
      known.forEach(function(record) { rows.push({ application: application, record: record }); });
      return true;
    });
    return coherent && rows.length ? { technology: technology, rows: rows } : null;
  }

  function applyBroadComplementaryEvidence(groups, references, unresolved) {
    references.forEach(function(configuration) {
      var evidence = coherentBroadEvidence(configuration);
      if (!evidence) return;
      var candidates = groups.filter(function(group) { return group.technology === evidence.technology; });
      if (!candidates.length) return;
      var evidenceCombined = evidence.rows.some(function(item) { return item.application === 'headlight_high_low_combined'; });
      var evidenceSeparate = evidence.rows.some(function(item) { return item.application === 'low_beam' || item.application === 'high_beam'; });
      var schemeMismatch = candidates.some(function(group) {
        var groupCombined = (group.positions.headlight_high_low_combined || []).length > 0;
        var groupSeparate = (group.positions.low_beam || []).length > 0 || (group.positions.high_beam || []).length > 0;
        return (evidenceCombined && groupSeparate) || (evidenceSeparate && groupCombined);
      });
      if (schemeMismatch) {
        unresolved.push({ type: 'broad-evidence-beam-scheme-mismatch', source_key: configuration.key, evidence_scheme: evidenceCombined ? 'combined' : 'separate' });
        return;
      }
      var fullyResolved = true;
      evidence.rows.forEach(function(item) {
        var evidenceBulb = normalizedBulbFact(item.record.bulb_size || item.record.canonical_bulb);
        var known = unique(candidates.reduce(function(values, group) {
          return values.concat((group.positions[item.application] || []).filter(function(record) { return record.fitment_type === 'replaceable_bulb'; }).map(function(record) { return normalizedBulbFact(record.bulb_size || record.canonical_bulb); }).filter(Boolean));
        }, []));
        if (known.some(function(value) { return value !== evidenceBulb; })) {
          unresolved.push({ type: 'broad-evidence-conflict', source_key: configuration.key, application: item.application, evidence_bulb: evidenceBulb, existing_bulbs: known });
          fullyResolved = false;
          return;
        }
        candidates.forEach(function(group) {
          var matching = (group.positions[item.application] || []).some(function(record) { return record.fitment_type === 'replaceable_bulb' && normalizedBulbFact(record.bulb_size || record.canonical_bulb) === evidenceBulb; });
          if (matching) return;
          group.positions[item.application] = (group.positions[item.application] || []).filter(function(record) { return record.fitment_type !== 'unknown'; });
          var resolved = Object.assign({}, item.record, {
            housing_type: item.record.housing_type && item.record.housing_type !== 'unknown' ? item.record.housing_type : group.optics,
            system_type: item.record.system_type && item.record.system_type !== 'unknown' ? item.record.system_type : group.technology,
            recommendation_allowed: false,
            applicability: group.applicability.slice()
          });
          mergeGroupRecord(group, item.application, resolved, '', 'broad-technology-scope-complement');
          group.resolvedUnknowns = (group.resolvedUnknowns || 0) + 1;
        });
      });
      if (fullyResolved && candidates.length === 1) {
        candidates[0].referenceAliases = unique((candidates[0].referenceAliases || []).concat(configuration.key));
      }
    });
  }

  function approvedPositionRecommendationSupport(configurations) {
    var support = {};
    (configurations || []).forEach(function(configuration) {
      if (!configuration || configuration.source_type !== 'existing_bri') return;
      Object.keys(configuration.positions || {}).forEach(function(application) {
        (configuration.positions[application] || []).forEach(function(record) {
          var bulb;
          var key;
          if (!record || record.recommendation_allowed !== true || record.fitment_type !== 'replaceable_bulb' || isFactoryLedRecord(record)) return;
          bulb = normalizedBulbFact(record.bulb_size || record.canonical_bulb);
          if (!bulb) return;
          key = application + '=' + bulb;
          if (!support[key]) support[key] = [];
          support[key] = unique(support[key].concat(record.id || []).concat(record.upstream_ids || []));
        });
      });
    });
    return support;
  }

  function applyApprovedPositionRecommendationSupport(groups, configurations) {
    var support = approvedPositionRecommendationSupport(configurations);
    (groups || []).forEach(function(group) {
      Object.keys(group.positions || {}).forEach(function(application) {
        (group.positions[application] || []).forEach(function(record) {
          var bulb;
          var sourceIds;
          if (!record || record.recommendation_allowed === true || record.fitment_type !== 'replaceable_bulb' || record.source_listed_unverified === true || isFactoryLedRecord(record) || group.technology === 'led') return;
          bulb = normalizedBulbFact(record.bulb_size || record.canonical_bulb);
          sourceIds = support[application + '=' + bulb];
          if (!bulb || !sourceIds || !sourceIds.length) return;
          record.recommendation_allowed = true;
          record.recommendation_applicability = unique((record.recommendation_applicability || []).concat(record.applicability || []).concat(group.applicability || []));
          record.recommendation_resolution = 'existing-bri-position-bulb-support';
          record.recommendation_support_ids = sourceIds.slice();
        });
      });
      if (group.output) {
        group.output.recommendation_allowed = Object.values(group.positions).some(function(rows) {
          return rows.some(function(record) { return record.recommendation_allowed === true; });
        });
      }
    });
  }

  function groupBaseLabel(group, technologyGroups) {
    var technology = group.technology;
    var optics = group.optics;
    var opticsVariants = unique(technologyGroups.map(function(item) { return item.optics; }));
    if (technology === 'led') return 'Factory LED' + (opticsVariants.length > 1 && optics !== 'unknown' ? ' ' + optics : '') + ' headlights';
    if (technology === 'hid') return 'Factory HID / Xenon' + (opticsVariants.length > 1 && optics !== 'unknown' ? ' ' + optics : '') + ' headlights';
    if (technology === 'halogen') return 'Halogen' + (opticsVariants.length > 1 && optics !== 'unknown' ? ' ' + optics : '') + ' headlights';
    return 'Factory lighting';
  }

  function groupResultSignature(group) {
    var invalid = false;
    var signature = Object.keys(group.positions || {}).sort().map(function(application) {
      var rows = group.positions[application] || [];
      if (rows.some(function(record) { return record.fitment_type === 'unknown'; })) invalid = true;
      var facts = unique(rows.map(recordFact)).sort();
      return application + '=' + facts.join(',');
    }).join(';');
    return invalid ? '' : signature;
  }

  function mergeEquivalentGroupsAcrossSources(groups) {
    var output = [];
    groups.forEach(function(group) {
      var signature = groupResultSignature(group);
      var match = output.filter(function(candidate) {
        var opticsCompatible = candidate.optics === group.optics || candidate.optics === 'unknown' || group.optics === 'unknown';
        var candidateSignature = groupResultSignature(candidate);
        var sharedApplicationsCompatible = Object.keys(candidate.positions || {}).filter(function(application) {
          return (candidate.positions[application] || []).length && (group.positions[application] || []).length;
        }).every(function(application) {
          return groupApplicationBulbSignature(candidate, application) === groupApplicationBulbSignature(group, application);
        });
        var sharedPrimaryApplication = HEADLIGHT_APPLICATIONS.some(function(application) {
          return groupApplicationBulbSignature(candidate, application) && groupApplicationBulbSignature(group, application);
        });
        return candidate.family !== group.family &&
          candidate.technology === group.technology &&
          opticsCompatible &&
          ((signature && candidateSignature === signature) || (sharedPrimaryApplication && sharedApplicationsCompatible && groupPrimaryBulbSignaturesCompatible(candidate, group)));
      })[0];
      if (!match) {
        output.push(group);
        return;
      }
      group.members.forEach(function(member) { if (match.members.indexOf(member) === -1) match.members.push(member); });
      match.legacyKeys = unique(match.legacyKeys.concat(group.legacyKeys));
      match.applicability = unique(match.applicability.concat(group.applicability));
      match.referenceAliases = unique((match.referenceAliases || []).concat(group.referenceAliases || []));
      match.resolvedUnknowns = (match.resolvedUnknowns || 0) + (group.resolvedUnknowns || 0);
      match.family = match.family === 'existing' || group.family === 'existing' ? 'existing' : 'factory';
      if (match.optics === 'unknown' && group.optics !== 'unknown') match.optics = group.optics;
      Object.keys(group.positions).forEach(function(application) {
        group.positions[application].forEach(function(record) {
          mergeGroupRecord(match, application, record, '', record.resolution || 'retained-source-record', true);
        });
      });
    });
    return output;
  }

  function groupBulbQualifier(group) {
    var labels = { low_beam: 'low', high_beam: 'high', headlight_high_low_combined: 'high/low' };
    return HEADLIGHT_APPLICATIONS.reduce(function(values, application) {
      var bulbs = unique((group.positions[application] || []).filter(function(record) { return record.fitment_type === 'replaceable_bulb'; }).map(function(record) { return record.bulb_size || record.canonical_bulb; }).filter(Boolean));
      if (bulbs.length === 1) values.push(bulbs[0] + ' ' + labels[application]);
      return values;
    }, []).join(' / ');
  }

  var BODY_STYLE_LABELS = {
    coupe: 'Coupe',
    sedan: 'Sedan',
    hatchback: 'Hatchback',
    wagon: 'Wagon',
    convertible: 'Convertible',
    cabriolet: 'Convertible',
    roadster: 'Roadster',
    liftback: 'Liftback',
    fastback: 'Fastback',
    pickup: 'Pickup',
    suv: 'SUV',
    van: 'Van',
    minivan: 'Minivan'
  };

  function groupBodyStyles(group) {
    var styles = [];
    (group.applicability || []).forEach(function(value) {
      Object.keys(BODY_STYLE_LABELS).forEach(function(key) {
        if (new RegExp('(?:^|[^a-z])' + key + '(?:$|[^a-z])', 'i').test(value)) styles.push(BODY_STYLE_LABELS[key]);
      });
    });
    return unique(styles);
  }

  function bodyStyleQualifier(group, peers) {
    var styles = groupBodyStyles(group);
    if (!styles.length || styles.length > 2) return '';
    var signature = styles.slice().sort().join('|');
    var distinct = peers.every(function(peer) {
      return peer === group || groupBodyStyles(peer).slice().sort().join('|') !== signature;
    });
    return distinct ? styles.join(' / ') : '';
  }

  function conciseApplicabilityQualifier(group, peers) {
    var values = (group.applicability || []).reduce(function(output, value) {
      if (/^other configurations with\b/i.test(value) || /not specified|not identified/i.test(value)) return output;
      if (groupBodyStyles({ applicability: [value] }).length) return output;
      return output.concat(String(value || '').split(/[|;,]/).map(function(part) {
        return part.trim()
          .replace(/\btrim\s+equipped\s+with\b/gi, 'with')
          .replace(/\btrim\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
      }).filter(Boolean));
    }, []);
    values = unique(values);
    var peerValues = peers.reduce(function(output, peer) {
      if (peer === group) return output;
      return output.concat((peer.applicability || []).reduce(function(parts, value) {
        return parts.concat(String(value || '').split(/[|;,]/).map(function(part) { return normalizeRulePart(part); }).filter(Boolean));
      }, []));
    }, []);
    var distinctive = values.filter(function(value) { return peerValues.indexOf(normalizeRulePart(value)) === -1; });
    if (!distinctive.length || distinctive.length > 4) return '';
    var label = distinctive.join(' / ');
    return label.length <= 52 ? label : '';
  }

  function groupApplicationBulbSignature(group, application) {
    var values = (group.positions[application] || []).map(function(record) {
      if (record.fitment_type === 'factory_led') return 'factory_led';
      if (record.fitment_type === 'replaceable_bulb') return normalizedBulbFact(record.bulb_size || record.canonical_bulb);
      return record.fitment_type || 'unknown';
    }).filter(Boolean);
    return unique(values).sort().join('|');
  }

  function groupPrimaryBulbSignature(group) {
    return HEADLIGHT_APPLICATIONS.map(function(application) {
      return application + '=' + groupApplicationBulbSignature(group, application);
    }).join(';');
  }

  function groupPrimaryBulbSignaturesCompatible(left, right) {
    var leftCombined = groupApplicationBulbSignature(left, 'headlight_high_low_combined');
    var rightCombined = groupApplicationBulbSignature(right, 'headlight_high_low_combined');
    var leftSeparate = groupApplicationBulbSignature(left, 'low_beam') || groupApplicationBulbSignature(left, 'high_beam');
    var rightSeparate = groupApplicationBulbSignature(right, 'low_beam') || groupApplicationBulbSignature(right, 'high_beam');
    if ((leftCombined && rightSeparate) || (rightCombined && leftSeparate)) return false;
    return HEADLIGHT_APPLICATIONS.every(function(application) {
      var leftValue = groupApplicationBulbSignature(left, application);
      var rightValue = groupApplicationBulbSignature(right, application);
      return !leftValue || !rightValue || leftValue === rightValue;
    });
  }

  function secondaryResultQualifier(group, groups) {
    var peers = groups.filter(function(candidate) {
      return candidate !== group &&
        candidate.technology === group.technology &&
        groupPrimaryBulbSignature(candidate) === groupPrimaryBulbSignature(group) &&
        groupResultSignature(candidate) !== groupResultSignature(group);
    });
    if (!peers.length) return '';
    var labels = {
      fog: 'fog',
      drl: 'DRL',
      parking: 'parking',
      front_turn_signal: 'front turn',
      rear_turn_signal: 'rear turn',
      reverse: 'reverse',
      tail: 'tail',
      brake: 'brake',
      side_marker: 'side marker',
      license_plate: 'license plate'
    };
    var applications = unique(Object.keys(group.positions || {}).concat(peers.reduce(function(values, peer) {
      return values.concat(Object.keys(peer.positions || {}));
    }, []))).filter(function(application) {
      if (HEADLIGHT_APPLICATIONS.indexOf(application) !== -1) return false;
      var signatures = unique([group].concat(peers).map(function(candidate) {
        return groupApplicationBulbSignature(candidate, application);
      }));
      return signatures.length > 1;
    });
    var priority = Object.keys(labels);
    applications.sort(function(left, right) {
      var leftIndex = priority.indexOf(left);
      var rightIndex = priority.indexOf(right);
      return (leftIndex === -1 ? priority.length : leftIndex) - (rightIndex === -1 ? priority.length : rightIndex);
    });
    for (var index = 0; index < applications.length; index += 1) {
      var application = applications[index];
      var bulbs = unique((group.positions[application] || []).filter(function(record) {
        return record.fitment_type === 'replaceable_bulb';
      }).map(function(record) {
        return record.bulb_size || record.canonical_bulb;
      }).filter(Boolean));
      if (bulbs.length === 1) return bulbs[0] + ' ' + (labels[application] || application.replace(/_/g, ' '));
      if (!bulbs.length && labels[application]) return 'No ' + labels[application] + '-light bulb result';
    }
    return '';
  }

  function finalizeDisplayGroups(groups) {
    groups.forEach(function(group) {
      var sameTechnology = groups.filter(function(item) { return item.technology === group.technology; });
      group.baseLabel = groupBaseLabel(group, sameTechnology);
    });
    groups.forEach(function(group) {
      var peers = groups.filter(function(item) { return item.baseLabel === group.baseLabel; });
      var duplicateLabel = peers.length > 1;
      var prefix = duplicateLabel ? bodyStyleQualifier(group, peers) || conciseApplicabilityQualifier(group, peers) : '';
      var qualifier = secondaryResultQualifier(group, groups);
      if (duplicateLabel && !prefix && !qualifier) qualifier = groupBulbQualifier(group);
      var label = (prefix ? prefix + ' · ' : '') + group.baseLabel + (qualifier ? ' · ' + qualifier : '');
      group.output = {
        key: 'group:' + stableDisplayGroupId(group.legacyKeys),
        label: label,
        source_type: group.family === 'factory' ? 'factory_reference' : 'existing_bri',
        scope_key: 'canonical:' + stableDisplayGroupId(group.legacyKeys),
        scope_type: 'canonical_display_group',
        confidence: group.members.reduce(function(best, configuration) { return configuration.confidence || best; }, 'possible'),
        recommendation_allowed: Object.values(group.positions).some(function(rows) { return rows.some(function(record) { return record.recommendation_allowed === true; }); }),
        positions: group.positions,
        applicability: group.applicability,
        display_group: true,
        resolved_unknown_positions: group.resolvedUnknowns || 0
      };
    });
    return groups.map(function(group) { return group.output; });
  }

  function resolveVehicleConfigurations(vehicle) {
    if (!vehicle) return { selectable: [], references: [], aliases: {}, unresolved: [], groups: [] };
    if (vehicle._briConfigurationResolution) return vehicle._briConfigurationResolution;
    var configurations = vehicle.configurations || [];
    var seeds = configurations.filter(seedConfiguration);
    var references = configurations.filter(function(configuration) { return seeds.indexOf(configuration) === -1; });
    var groups = groupSeedConfigurations(seeds);
    groups = mergeComplementaryGroups(groups);
    var unresolved = [];
    applyBroadComplementaryEvidence(groups, references, unresolved);
    groups = mergeEquivalentGroupsAcrossSources(groups);
    var selectable = finalizeDisplayGroups(groups);
    applyApprovedPositionRecommendationSupport(groups, configurations);
    var aliases = {};
    groups.forEach(function(group, index) {
      group.legacyKeys.forEach(function(key) { aliases[key] = selectable[index].key; });
      (group.referenceAliases || []).forEach(function(key) { aliases[key] = selectable[index].key; });
    });
    var resolution = {
      selectable: selectable,
      references: references,
      aliases: aliases,
      unresolved: unresolved,
      groups: groups,
      original_configuration_count: configurations.length,
      original_selectable_count: configurations.filter(function(configuration) {
        if (configuration.scope_type === 'source_unqualified' || isGenericConfigurationLabel(configuration)) return false;
        return configuration.source_type !== 'public_reference' || !publicConfigurationIsDuplicate(configuration, configurations.filter(function(item) { return item.source_type !== 'public_reference'; }));
      }).length
    };
    try { Object.defineProperty(vehicle, '_briConfigurationResolution', { value: resolution, configurable: true }); }
    catch (error) { vehicle._briConfigurationResolution = resolution; }
    return resolution;
  }

  function recordMatchesConfiguration(application, record, configuration) {
    var bulb = normalizedBulbFact(record && (record.bulb_size || record.canonical_bulb));
    return (configuration && configuration.positions && configuration.positions[application] || []).some(function(candidate) {
      return bulb && normalizedBulbFact(candidate.bulb_size || candidate.canonical_bulb) === bulb;
    });
  }

  function configurationCoveredBy(configuration, candidates) {
    var rows = configurationRows(configuration);
    return rows.length > 0 && rows.every(function(item) {
      return candidates.some(function(candidate) { return recordMatchesConfiguration(item.application, item.record, candidate); });
    });
  }

  function publicConfigurationIsDuplicate(configuration, nonPublic) {
    var technology = configurationTechnology(configuration);
    if (!technology || configuration.source_type !== 'public_reference') return false;
    return nonPublic.some(function(candidate) {
      return configurationTechnology(candidate) === technology && configurationCoveredBy(configuration, [candidate]);
    });
  }

  function selectableConfigurations(vehicle) {
    return resolveVehicleConfigurations(vehicle).selectable;
  }

  function nonSelectableConfigurations(vehicle) {
    return resolveVehicleConfigurations(vehicle).references;
  }

  function createReferenceConfiguration(vehicle) {
    var configs = nonSelectableConfigurations(vehicle);
    var positions = {};
    configs.forEach(function(configuration) {
      Object.keys(configuration.positions || {}).forEach(function(application) {
        positions[application] = (positions[application] || []).concat(configuration.positions[application] || []);
      });
    });
    if (!Object.keys(positions).length) return null;
    return {
      key: '__reference_sizes__',
      label: UI_COPY.referencePackage,
      source_type: 'reference_only',
      scope_type: 'reference_only',
      positions: positions,
      source_keys: configs.map(function(configuration) { return configuration.key; })
    };
  }

  function supplementalGroups(configuration, selectable) {
    return getPositionGroups(configuration).map(function(group) {
      var rows = group.rows.filter(function(record) {
        if (record.recommendation_allowed === true) {
          return !selectable.some(function(candidate) {
            return (candidate.positions && candidate.positions[group.key] || []).some(function(candidateRecord) {
              return candidateRecord.recommendation_allowed === true && candidateRecord.fitment_type === record.fitment_type && normalizedBulbFact(candidateRecord.bulb_size || candidateRecord.canonical_bulb) === normalizedBulbFact(record.bulb_size || record.canonical_bulb);
            });
          });
        }
        return !selectable.some(function(candidate) { return recordMatchesConfiguration(group.key, record, candidate); });
      });
      return { key: group.key, label: group.label, rows: rows };
    }).filter(function(group) { return group.rows.length; });
  }

  function renderSupplementalRecommendationLinks(record, application, rulesData) {
    var recommendations = getRecommendations(record, application, rulesData);
    if (!recommendations.length) return '';
    return '<div class="vehicle-selector-placeholder__supplemental-recommendations"><p><strong>Options for this bulb size</strong></p><ul>' + recommendations.map(function(item) {
      return '<li><a href="' + escapeHtml(getProductUrl(item.handle)) + '">' + escapeHtml(item.label || readableHandle(item.handle)) + '</a>' + (item.reason ? '<span>' + escapeHtml(item.reason) + '</span>' : '') + '</li>';
    }).join('') + '</ul></div>';
  }

  function renderSupplemental(container, vehicle, selectedConfiguration, collectionsUrl, rulesData) {
    var selectable = selectableConfigurations(vehicle);
    var excluded = selectedConfiguration && selectedConfiguration.source_keys || [];
    var configs = nonSelectableConfigurations(vehicle).filter(function(configuration) { return excluded.indexOf(configuration.key) === -1; });
    var blocks = configs.map(function(configuration) {
      return { configuration: configuration, groups: supplementalGroups(configuration, selectable) };
    }).filter(function(item) { return item.groups.length; });
    var html = '';
    if (blocks.length) {
      html += '<h4 class="vehicle-selector-placeholder__source-heading">' + UI_COPY.otherBulbsHeading + '</h4><p class="vehicle-selector-placeholder__source-intro">' + UI_COPY.otherBulbsIntro + '</p>' + blocks.map(function(item) {
        var configuration = item.configuration;
        var conditions = conditionText(configuration);
        var generic = isGenericConfigurationLabel(configuration);
        if (generic) conditions.push('These bulb sizes are not assigned to the selected factory lighting system. Confirm the installed bulb before using a product option.');
        var label = generic ? 'Bulb sizes requiring confirmation' : configuration.scope_type === 'source_unqualified' ? 'Additional positions' : customerConfigurationLabel(configuration);
        return '<details class="vehicle-selector-placeholder__source-group"><summary>' + escapeHtml(label) + ' <span>(' + item.groups.length + ' positions)</span></summary>' +
          (conditions.length ? '<p><strong>Identification help:</strong> ' + escapeHtml(conditions.join('; ')) + '</p>' : '') +
          '<dl>' + item.groups.map(function(group) {
            return '<div><dt>' + escapeHtml(group.label) + '</dt><dd>' + group.rows.map(function(record) {
              return '<div class="vehicle-selector-placeholder__supplemental-record">' + renderBulbValue(record, collectionsUrl) + renderApplicabilityLine(record) + renderSupplementalRecommendationLinks(record, group.key, rulesData) + '</div>';
            }).join('') + '</dd></div>';
          }).join('') + '</dl></details>';
      }).join('');
    }
    container.innerHTML = html;
  }

  function renderVehicleResult(state, configuration) {
    var groups = getPositionGroups(configuration);
    var activeKey = groups.length ? groups[0].key : '';
    state.currentGroups = groups;
    state.resultHeading.textContent = UI_COPY.resultsTitle;
    state.resultVehicle.textContent = [state.vehicle.year, state.vehicle.make, state.vehicle.model].filter(Boolean).join(' ');
    renderSummary(state.resultSummary, groups, state.collectionsUrl);
    renderTabs(state.resultTabs, groups, activeKey);
    state.resultConfidence.textContent = configuration.scope_type === 'reference_only' ? '' : customerConfigurationLabel(configuration);
    renderRecommendations(state.resultRecommendations, groups[0], state.rulesData, state.collectionsUrl);
    renderSupplemental(state.resultSupplemental, state.vehicle, configuration, state.collectionsUrl, state.rulesData);
    state.results.classList.remove('vehicle-selector-placeholder__results--hidden');
    state.fallback.classList.add('vehicle-selector-placeholder__fallback--hidden');
  }

  function updateConfigurationApplicability(state, configuration) {
    if (!state || !state.configurationApplicability) return;
    var values = unique(configuration && configuration.applicability || []).filter(function(value) {
      return !/^other configurations with\b/i.test(value) &&
        !/trim\/package not specified/i.test(value) &&
        !/factory package not identified/i.test(value);
    });
    state.configurationApplicability.textContent = values.length ? 'Applies to: ' + values.join('; ') : '';
    state.configurationApplicability.hidden = !values.length;
  }

  function clearResult(state) {
    recommendationRenderId += 1;
    state.currentGroups = [];
    state.results.classList.add('vehicle-selector-placeholder__results--hidden');
    state.resultVehicle.textContent = '';
    state.resultSummary.innerHTML = '';
    state.resultTabs.innerHTML = '';
    state.resultConfidence.textContent = '';
    state.resultRecommendations.innerHTML = '';
    state.resultSupplemental.innerHTML = '';
  }

  function configOptionLabel(config) {
    return customerConfigurationLabel(config);
  }

  function findConfig(vehicle, key) {
    var resolved = resolveVehicleConfigurations(vehicle);
    var canonical = resolved.aliases[key] || key;
    return resolved.selectable.filter(function(config) { return config.key === canonical; })[0] || (vehicle && (vehicle.configurations || []).filter(function(config) { return config.key === key; })[0]) || null;
  }

  function findSelectableConfig(vehicle, key) {
    var resolved = resolveVehicleConfigurations(vehicle);
    var canonical = resolved.aliases[key] || key;
    return resolved.selectable.filter(function(config) { return config.key === canonical; })[0] || null;
  }

  function selectionFromControls(state) {
    return { year: state.year.value, make: state.make.value, model: state.model.value, configuration: state.trim.value };
  }

  function renderStatus(state, message) {
    state.status.textContent = message || '';
  }

  function hasVehicleSelection(state) {
    return !!(state && state.year && state.make && state.model && state.trim && (state.year.value || state.make.value || state.model.value || state.trim.value));
  }

  function updateClearControl(state) {
    if (!state || !state.clear) return;
    var hasSelection = hasVehicleSelection(state);
    state.clear.hidden = !hasSelection;
    state.clear.disabled = !hasSelection;
  }

  function resetVehicleState(state, message) {
    state.requestEpoch += 1;
    state.year.value = '';
    clearSelect(state.make, 'Make');
    clearSelect(state.model, 'Model');
    clearSelect(state.trim, state.trimLabel);
    state.vehicle = null;
    state.selectableConfigs = [];
    updateConfigurationApplicability(state, null);
    clearResult(state);
    state.fallback.classList.add('vehicle-selector-placeholder__fallback--hidden');
    renderStatus(state, message || '');
    updateClearControl(state);
  }

  function activeSelectorStates(states) {
    return (states || []).filter(function(state) {
      return state && state.root && state.root.isConnected !== false;
    });
  }

  function clearVehicleStates(states, origin, storage) {
    var emptySelection = { year: '', make: '', model: '', configuration: '' };
    clearStoredSelection(storage);
    updateSelectionUrl(emptySelection);
    activeSelectorStates(states).forEach(function(state) {
      resetVehicleState(state, UI_COPY.clearedStatus);
    });
    if (origin && origin.year && typeof origin.year.focus === 'function') origin.year.focus();
  }

  function resetAfterYear(state) {
    clearSelect(state.make, 'Make');
    clearSelect(state.model, 'Model');
    clearSelect(state.trim, state.trimLabel);
    state.vehicle = null;
    updateConfigurationApplicability(state, null);
    state.requestEpoch += 1;
    clearResult(state);
  }

  function resetAfterMake(state) {
    clearSelect(state.model, 'Model');
    clearSelect(state.trim, state.trimLabel);
    state.vehicle = null;
    updateConfigurationApplicability(state, null);
    state.requestEpoch += 1;
    clearResult(state);
  }

  function resetAfterModel(state) {
    clearSelect(state.trim, state.trimLabel);
    state.vehicle = null;
    updateConfigurationApplicability(state, null);
    state.requestEpoch += 1;
    clearResult(state);
  }

  function applyYear(state, value) {
    resetAfterYear(state);
    if (!value) return false;
    if (!isSupportedYear(value, state.minimumYear)) {
      state.year.value = '';
      renderStatus(state, 'Vehicle years before ' + state.minimumYear + ' are not supported. Choose ' + state.minimumYear + ' or newer.');
      return false;
    }
    var entry = yearEntry(state.indexData, value);
    if (!entry) return false;
    setOptions(state.make, 'Make', entry.makes || [], function(item) { return item.make; }, function(item) { return item.make; });
    return true;
  }

  function applyMake(state, value) {
    resetAfterMake(state);
    if (!value) return false;
    var entry = makeEntry(state.indexData, state.year.value, value);
    if (!entry) return false;
    setOptions(state.model, 'Model', sortText((entry.models || []).slice()));
    return true;
  }

  function loadSelectedVehicle(state) {
    resetAfterModel(state);
    if (!state.model.value) return Promise.resolve(null);
    if (!isSupportedYear(state.year.value, state.minimumYear) || !modelExists(state.indexData, state.year.value, state.make.value, state.model.value)) {
      renderStatus(state, 'This vehicle selection is not available. Choose a supported ' + state.minimumYear + '+ vehicle.');
      return Promise.resolve(null);
    }
    var entry = makeEntry(state.indexData, state.year.value, state.make.value);
    var shardUrl = entry && state.shardMap[entry.slug];
    if (!shardUrl) {
      renderStatus(state, 'The selected vehicle data file is not available.');
      return Promise.resolve(null);
    }
    var epoch = ++state.requestEpoch;
    renderStatus(state, 'Loading vehicle configurations…');
    return fetchJson(shardUrl).then(function(shard) {
      if (epoch !== state.requestEpoch) return null;
      var vehicle = findVehicle(shard, state.year.value, state.make.value, state.model.value, state.minimumYear);
      if (!vehicle) throw new Error('Vehicle record missing from make shard.');
      state.vehicle = vehicle;
      state.selectableConfigs = selectableConfigurations(vehicle);
      setOptions(state.trim, state.trimLabel, state.selectableConfigs, function(config) { return config.key; }, configOptionLabel);
      updateConfigurationApplicability(state, null);
      if (!state.selectableConfigs.length && createReferenceConfiguration(vehicle)) {
        state.trim.options[0].textContent = UI_COPY.referencePackage;
        renderStatus(state, UI_COPY.referenceGuidance);
      } else {
        renderStatus(state, '');
      }
      return vehicle;
    }).catch(function() {
      if (epoch === state.requestEpoch) renderStatus(state, 'Vehicle configurations could not be loaded. Please try again.');
      return null;
    });
  }

  function restoreSelection(state, restored) {
    var selection = restored.selection;
    if (restored.unsupportedYear) {
      clearStoredSelection(getFinderStorage(global));
      updateSelectionUrl({ year: '', make: '', model: '', configuration: '' });
      clearResult(state);
      renderStatus(state, 'A saved ' + restored.unsupportedYear + ' vehicle was cleared because this finder supports model years ' + state.minimumYear + ' and newer.');
      updateClearControl(state);
      return Promise.resolve(false);
    }
    if (!selection.year || !selection.make || !selection.model) {
      updateClearControl(state);
      return Promise.resolve(false);
    }
    if (!yearEntry(state.indexData, selection.year)) {
      updateClearControl(state);
      return Promise.resolve(false);
    }
    state.year.value = String(selection.year);
    if (!applyYear(state, selection.year)) {
      updateClearControl(state);
      return Promise.resolve(false);
    }
    state.make.value = selection.make;
    if (!applyMake(state, selection.make)) {
      updateClearControl(state);
      return Promise.resolve(false);
    }
    state.model.value = selection.model;
    updateClearControl(state);
    return loadSelectedVehicle(state).then(function(vehicle) {
      if (!vehicle) return false;
      var config = findSelectableConfig(vehicle, selection.configuration);
      if (config) {
        state.trim.value = config.key;
        updateConfigurationApplicability(state, config);
        renderVehicleResult(state, config);
        if (selection.configuration !== config.key) {
          selection.configuration = config.key;
          saveSelection(selection, getFinderStorage(global));
          updateSelectionUrl(selection);
        }
        return true;
      }
      var reference = !state.selectableConfigs.length ? createReferenceConfiguration(vehicle) : null;
      if (reference) {
        renderVehicleResult(state, reference);
        return true;
      }
      if (selection.configuration) renderStatus(state, 'Choose the current factory lighting configuration.');
      return false;
    });
  }

  function initSelector(root) {
    if (!root || root.dataset.vehicleSelectorReady === 'true') return;
    var state = {
      root: root,
      minimumYear: Number(root.dataset.minModelYear || MIN_MODEL_YEAR),
      indexUrl: root.dataset.fitmentIndexUrl,
      rulesUrl: root.dataset.rulesUrl,
      shardMap: parseShardMap(root),
      year: root.querySelector('[data-vehicle-year]'),
      make: root.querySelector('[data-vehicle-make]'),
      model: root.querySelector('[data-vehicle-model]'),
      trim: root.querySelector('[data-vehicle-trim]'),
      configurationApplicability: root.querySelector('[data-vehicle-configuration-applicability]'),
      submit: root.querySelector('[data-vehicle-submit]'),
      clear: root.querySelector('[data-vehicle-clear]'),
      status: root.querySelector('[data-vehicle-status]'),
      fallback: root.querySelector('[data-vehicle-fallback]'),
      results: root.querySelector('[data-vehicle-results]'),
      resultHeading: root.querySelector('[data-result-heading]'),
      resultVehicle: root.querySelector('[data-result-vehicle]'),
      resultSummary: root.querySelector('[data-result-summary]'),
      resultTabs: root.querySelector('[data-result-tabs]'),
      resultConfidence: root.querySelector('[data-result-confidence]'),
      resultRecommendations: root.querySelector('[data-result-recommendations]'),
      resultSupplemental: root.querySelector('[data-result-supplemental]'),
      collectionsUrl: parseCollectionsUrl(root.dataset.collectionsUrl),
      trimLabel: UI_COPY.trimLabel,
      requestEpoch: 0,
      currentGroups: [],
      selectableConfigs: [],
      vehicle: null,
      indexData: null,
      rulesData: {}
    };
    if (state.trim) state.trimLabel = state.trim.getAttribute('data-vehicle-trim-label') || state.trimLabel;
    if (!state.indexUrl || !state.rulesUrl || !state.year || !state.make || !state.model || !state.trim || !state.submit || !state.clear || !state.status || !state.fallback || !state.results || !state.resultSummary || !state.resultTabs || !state.resultRecommendations || !state.resultSupplemental) return;
    root.dataset.vehicleSelectorReady = 'true';
    selectorStates.push(state);
    updateClearControl(state);

    state.year.addEventListener('change', function() {
      renderStatus(state, '');
      applyYear(state, state.year.value);
      updateClearControl(state);
    });
    state.make.addEventListener('change', function() {
      renderStatus(state, '');
      applyMake(state, state.make.value);
      updateClearControl(state);
    });
    state.model.addEventListener('change', function() {
      renderStatus(state, '');
      loadSelectedVehicle(state);
      updateClearControl(state);
    });
    state.trim.addEventListener('change', function() {
      renderStatus(state, '');
      clearResult(state);
      updateConfigurationApplicability(state, findSelectableConfig(state.vehicle, state.trim.value));
      updateClearControl(state);
    });
    state.clear.addEventListener('click', function() {
      clearVehicleStates(selectorStates, state, getFinderStorage(global));
    });
    state.submit.addEventListener('click', function() {
      clearResult(state);
      var selection = selectionFromControls(state);
      if (!isSupportedYear(selection.year, state.minimumYear)) {
        renderStatus(state, 'Choose a model year of ' + state.minimumYear + ' or newer.');
        return;
      }
      var config = findSelectableConfig(state.vehicle, selection.configuration);
      if (!config) {
        var reference = state.vehicle && !state.selectableConfigs.length ? createReferenceConfiguration(state.vehicle) : null;
        if (reference) {
          renderVehicleResult(state, reference);
          renderStatus(state, '');
          selection.configuration = '';
          saveSelection(selection, getFinderStorage(global));
          updateSelectionUrl(selection);
          updateClearControl(state);
          return;
        }
        renderStatus(state, 'Choose a year, make, model, and trim / factory lighting. ' + UI_COPY.unknownHeadlightHelp);
        state.fallback.classList.remove('vehicle-selector-placeholder__fallback--hidden');
        return;
      }
      renderVehicleResult(state, config);
      updateConfigurationApplicability(state, config);
      renderStatus(state, '');
      saveSelection(selection, getFinderStorage(global));
      updateSelectionUrl(selection);
      updateClearControl(state);
    });
    state.resultTabs.addEventListener('click', function(event) {
      var button = event.target && event.target.closest ? event.target.closest('[data-position-key]') : null;
      if (!button || !state.resultTabs.contains(button)) return;
      var key = button.getAttribute('data-position-key');
      var group = state.currentGroups.filter(function(item) { return item.key === key; })[0];
      if (!group) return;
      setActiveTab(state.resultTabs, key);
      renderRecommendations(state.resultRecommendations, group, state.rulesData, state.collectionsUrl);
    });
    state.resultTabs.addEventListener('keydown', function(event) {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(event.key) === -1) return;
      var buttons = Array.prototype.slice.call(state.resultTabs.querySelectorAll('[data-position-key]'));
      var current = buttons.indexOf(global.document.activeElement);
      if (current < 0 || !buttons.length) return;
      event.preventDefault();
      var next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : event.key === 'ArrowLeft' ? (current - 1 + buttons.length) % buttons.length : (current + 1) % buttons.length;
      buttons[next].focus();
      buttons[next].click();
    });

    Promise.all([fetchJson(state.indexUrl), fetchJson(state.rulesUrl)]).then(function(responses) {
      state.indexData = responses[0] || {};
      state.rulesData = responses[1] || {};
      var years = (state.indexData.years || []).map(function(entry) { return Number(entry.year); }).filter(function(year) { return isSupportedYear(year, state.minimumYear); });
      setOptions(state.year, 'Year', years.sort(function(a, b) { return b - a; }));
      var stored = readStoredSelection(getFinderStorage(global));
      var restored = readRestoredSelection(global.location && global.location.search, stored, state.minimumYear);
      return restoreSelection(state, restored);
    }).catch(function() {
      renderStatus(state, 'Vehicle finder data is not available right now.');
    });
  }

  function initAll(container) {
    var scope = container || global.document;
    Array.prototype.forEach.call(scope.querySelectorAll('[data-section-type="vehicle-selector-placeholder"]'), initSelector);
  }

  var testApi = {
    MIN_MODEL_YEAR: MIN_MODEL_YEAR,
    UI_COPY: UI_COPY,
    normalizeIdentityPart: normalizeIdentityPart,
    normalizeRulePart: normalizeRulePart,
    vehicleKey: vehicleKey,
    isSupportedYear: isSupportedYear,
    readRestoredSelection: readRestoredSelection,
    readStoredSelection: readStoredSelection,
    saveSelection: saveSelection,
    clearStoredSelection: clearStoredSelection,
    getFinderStorage: getFinderStorage,
    buildSelectionUrl: buildSelectionUrl,
    yearEntry: yearEntry,
    makeEntry: makeEntry,
    modelExists: modelExists,
    findVehicle: findVehicle,
    getPositionGroups: getPositionGroups,
    isFactoryLedRecord: isFactoryLedRecord,
    isRecommendationEligible: isRecommendationEligible,
    getRecommendationRuleKeys: getRecommendationRuleKeys,
    conditionText: conditionText,
    parseCollectionsUrl: parseCollectionsUrl,
    isStandaloneBulbCode: isStandaloneBulbCode,
    bulbCodeHandle: bulbCodeHandle,
    bulbCollectionPath: bulbCollectionPath,
    renderBulbSizeLinks: renderBulbSizeLinks,
    resolveVehicleConfigurations: resolveVehicleConfigurations,
    selectableConfigurations: selectableConfigurations,
    createReferenceConfiguration: createReferenceConfiguration,
    configOptionLabel: configOptionLabel,
    customerConfigurationLabel: customerConfigurationLabel,
    recordApplicability: recordApplicability,
    renderInformationalRows: renderInformationalRows,
    renderRecommendations: renderRecommendations,
    renderSupplemental: renderSupplemental,
    renderVehicleResult: renderVehicleResult,
    updateConfigurationApplicability: updateConfigurationApplicability,
    findSelectableConfig: findSelectableConfig,
    restoreSelection: restoreSelection,
    hasVehicleSelection: hasVehicleSelection,
    updateClearControl: updateClearControl,
    resetVehicleState: resetVehicleState,
    clearVehicleStates: clearVehicleStates
  };

  if (typeof module === 'object' && module.exports) module.exports = testApi;
  global.BRIVehicleFinderTest = testApi;

  if (!global.document) return;
  if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', function() { initAll(); });
  else initAll();
  global.document.addEventListener('shopify:section:load', function(event) { initAll(event.target); });
})(typeof window !== 'undefined' ? window : globalThis);
